import { withAsyncContext as _withAsyncContext, defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, normalizeClass as _normalizeClass, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createSlots as _createSlots, renderSlot as _renderSlot, isRef as _isRef, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import type { NavigationMenuItem } from "#ui/types";

import type Organisation from "../../../backend/app/models/organisation";

export default /*@__PURE__*/_defineComponent({
  __name: 'dashboard',
  async setup(__props) {

let __temp: any, __restore: any

const colorMode = useColorMode();
const themeColor = computed(() => (colorMode.value === "dark" ? "#171717" : "#f5f5f5"));

useHead({
  meta: [{ name: "theme-color", content: themeColor.value }],
});

const auth = useAuthStore();
const { can } = useAbac();

const { isHelpSlideoverOpen } = useDashboard();
const familyGroupsStore = useGroupStore();
const communicationStore = useCommunicationStore();
const showAiChat = ref(false);

;(
  ([__temp,__restore] = _withAsyncContext(async () => callOnce(async () => {
  await communicationStore.fetchCount();
}))),
  await __temp,
  __restore()
);

const notifCount = computed(() => {
  return communicationStore.count > 0 ? communicationStore.count : undefined;
});
const transmitStore = useTransmitStore();

const organisation = computed(() => auth.orga as Partial<Organisation>);
const noGroupAccess = computed(() => familyGroupsStore.userGroups.length === 0);

// Modal state for command palette
const searchTerm = ref("");

// Define the ref for groups, initially empty or based on a default
const groups = computed(() =>
  familyGroupsStore.userGroups.map((group, index) => ({
    id: group.id,
    label: group.name,
    color: group.color,
    icon: `i-${group.icon}`,
    slot: "customChild" as const,
    // class: `bg-${group.color} dark:bg-${group.color} text-${group.color}`,
    // to: `/group/${group.id}`,
    tooltip: {
      text: group.description,
      shortcuts: ["G", index + 1],
    },
  })),
);

// Track if subscriptions have been initialized to prevent duplicates
const subscriptionsInitialized = ref(false);
const newPasswordModalOpen = ref(false);

// Initialize transmit subscriptions when user and orga are available
const initializeTransmitSubscriptions = () => {
  if (!import.meta.client) return;
  if (subscriptionsInitialized.value) return;

  if (auth.user) {
    subscriptionsInitialized.value = true;
    try {
      // Fire-and-forget: don't block rendering/navigation on SSE handshakes.
      // The store handles errors internally and re-arms via visibilitychange.
      transmitStore.init(auth.user, auth.orga, familyGroupsStore.currentSelectedGroup?.id);
    } catch (error) {
      console.error("Failed to initialize transmit subscriptions:", error);
      subscriptionsInitialized.value = false;
    }
  }
};

// Use watch with immediate:true to handle both mount and auth changes
watch(
  [() => auth.user, () => auth.orga, () => familyGroupsStore.currentSelectedGroup?.id],
  () => {
    if (subscriptionsInitialized.value) return;
    initializeTransmitSubscriptions();
  },
  { immediate: true },
);

// Watch for need_new_password changes to open/close modal
watch(
  () => auth.user?.need_new_password,
  (needNewPassword) => {
    if (needNewPassword) {
      newPasswordModalOpen.value = true;
    } else {
      newPasswordModalOpen.value = false;
    }
  },
  { immediate: true },
);

// CRITICAL: Add cleanup on unmount to prevent handler accumulation across navigations
onBeforeUnmount(async () => {
  await transmitStore.cleanup(true);
  subscriptionsInitialized.value = false;
});

const canAccessStaff = computed(() => {
  return auth.permissions?.hrDashboard?.includes("index") ?? false;
});

const sidebarLinks = computed(() => {
  const links: NavigationMenuItem[] = [
    {
      label: "Home",
      icon: "i-solar:home-smile-angle-outline",
      description: "Go to home",
      to: "/user/",
    },
    {
      label: "Notifications",
      icon: "i-heroicons-inbox",
      description: "Go to notifications",
      to: "/inbox",
      slot: "comms",
      badge: notifCount.value,
    },
    {
      label: "Tasks",
      icon: "i-heroicons-pencil-square",
      to: "/tasks",
      description: "Task Overview",
    },
    {
      label: "Observations",
      icon: "i-heroicons-eye",
      to: "/observations",
      description: "Observation List",
    },
    {
      label: "Direct Work",
      icon: "i-heroicons-academic-cap",
      to: "/directworks",
      description: "Direct Work Overview",
    },
    {
      label: "Safeguarding",
      icon: "i-material-symbols:security-sharp",
      to: "/safeguarding",
      description: "Safeguarding Overview",
    },
  ];

  if (canAccessStaff.value) {
    links.push({
      label: "Staff",
      icon: "i-fluent:people-settings-20-filled",
      to: "/hr",
      description: "HR Management",
    });
  }

  return links;
});

const noOrgaLinks = [
  {
    label: "Home",
    icon: "i-heroicons-squares-2x2",
    to: "/user/",
    tooltip: {
      text: "Home",
      shortcuts: ["G", "H"],
    },
  },
  {
    label: "Notifications",
    icon: "i-heroicons-inbox",
    to: "/inbox",
    badge: notifCount.value,
  },
] satisfies NavigationMenuItem[];

const residentsSection = computed(() => [
  {
    id: "residents",
    label: "Residents",
    icon: "i-heroicons-user-group",
    to: "/residents/",
    disabled: organisation.value ? false : true,
    tooltip: {
      text: "Residents",
      shortcuts: ["G", "R"],
    },
  },
  {
    id: "children",
    label: "Children",
    icon: "i-ic:baseline-child-friendly",
    to: "/children/",
    disabled: organisation.value ? false : true,
    tooltip: {
      text: "Children",
      shortcuts: ["G", "C"],
    },
  },
]);

const footerLinks = computed(() => {
  const links: NavigationMenuItem[] = [
    {
      label: "Settings",
      icon: "i-heroicons-cog-8-tooth",
      to: "/settings/",
    },
    {
      label: "Help & Support",
      icon: "i-heroicons-question-mark-circle",
      to: "/help/",
      // click: () => (isHelpSlideoverOpen.value = true),
    },
  ];

  if (can("users", "invite")) {
    links.push({
      label: "Invite people",
      icon: "i-heroicons-plus",
      to: "/settings/team",
    });
  }

  return links;
});

// Use the cookie directly from the auth store - it's already SSR-friendly
// The collapsed state is managed by the UDashboardSidebar component via v-model

// Navigation items
const navigationItems = computed(
  () =>
    [
      {
        id: "home",
        label: "Home",
        icon: "i-solar:home-smile-angle-outline",
        to: "/user/",
        kbds: ["G", "H"],
      },
      {
        id: "inbox",
        label: "Notifications",
        icon: "i-heroicons-inbox",
        to: "/inbox",
        badge: notifCount.value,
        kbds: ["G", "I"],
      },
      ...(can("users", "index")
        ? [
            {
              id: "team",
              label: "Team Members",
              icon: "i-heroicons-user-group",
              to: "/settings/team",
              disabled: organisation.value ? false : true,
            },
          ]
        : []),
      {
        id: "tasks",
        label: "Tasks",
        icon: "i-heroicons-pencil-square",
        to: "/tasks",
        disabled: organisation.value ? false : true,
        kbds: ["G", "T"],
      },
      {
        id: "observations",
        label: "Observations",
        icon: "i-heroicons-eye",
        to: "/observations",
        disabled: organisation.value ? false : true,
        kbds: ["G", "O"],
      },
      {
        id: "direct-work",
        label: "Direct Work",
        icon: "i-heroicons-academic-cap",
        to: "/directworks",
        disabled: organisation.value ? false : true,
        kbds: ["G", "D"],
      },
    ] satisfies NavigationMenuItem[],
);

// Group shortcut keys - using numbers for easy group switching (G+1, G+2, etc.)
const kkbd = ["1", "2", "3", "4", "5", "6", "7", "8"];
// Residents & Groups items (groups don't have kbds here - shortcuts are handled separately)
const residentsItems = computed(() => [
  {
    id: "residents-list",
    label: "All Residents",
    icon: "i-heroicons-user-group",
    to: "/residents/",
    disabled: organisation.value ? false : true,
    kbds: ["G", "R"],
  },
  {
    id: "children-list",
    label: "All Children",
    icon: "i-heroicons-user-group",
    to: "/children/",
    disabled: organisation.value ? false : true,
    kbds: ["G", "C"],
  },
  ...groups.value.map((group, index) => ({
    id: group.id,
    label: group.label,
    icon: group.icon,
    // Use onSelect instead of 'to' to switch group without navigation
    onSelect: () => {
      try {
        const fullGroup = familyGroupsStore.userGroups.find((g) => g.id === group.id);
        if (!fullGroup) {
          console.warn(`Group with id ${group.id} not found`);
          return;
        }
        familyGroupsStore.setCurrentSelectedGroup(fullGroup);
      } catch (error) {
        console.error("Failed to switch group:", error);
      }
    },
    // Group shortcuts shown in command palette
    kbds: ["G", kkbd[index]],
  })),
]);

// Settings items
const settingsItems = computed(() => {
  const items: NavigationMenuItem[] = [
    {
      id: "general-settings",
      label: "General Settings",
      icon: "i-heroicons-cog-8-tooth",
      to: "/settings",
      kbds: ["G", "S"],
    },
  ];

  if (can("users", "index")) {
    items.push({
      id: "staff-settings",
      label: "Staff Settings",
      icon: "i-heroicons-user-group",
      to: "/settings/team",
    });
  }

  items.push({
    id: "notification-settings",
    label: "Notification Settings",
    icon: "i-heroicons-bell",
    to: "/settings/",
  });

  if (can("users", "invite")) {
    items.push({
      id: "invite-people",
      label: "Invite People",
      icon: "i-heroicons-plus",
      to: "/settings/team",
    });
  }

  return items;
});

// Help items
const helpItems = computed(() => [
  {
    id: "help-support",
    label: "Help & Support",
    icon: "i-heroicons-question-mark-circle",
    to: "/help",
    // onSelect: () => (isHelpSlideoverOpen.value = true),
  },
]);

// Final command palette groups
const commandGroups = computed(() => [
  {
    id: "navigation",
    key: "navigation",
    label: searchTerm.value ? `Navigation matching "${searchTerm.value}"...` : "Navigation",
    items: navigationItems.value,
    ignoreFilter: false,
  },
  {
    id: "residents",
    key: "residents",
    label: searchTerm.value
      ? `Residents & Groups matching "${searchTerm.value}"...`
      : "Residents & Groups",
    items: residentsItems.value,
    ignoreFilter: false,
  },
  {
    id: "settings",
    key: "settings",
    label: searchTerm.value ? `Settings matching "${searchTerm.value}"...` : "Settings",
    items: settingsItems.value,
    ignoreFilter: false,
  },
  {
    id: "help",
    key: "help",
    label: "Support",
    items: helpItems.value,
    ignoreFilter: false,
  },
]);

// Register navigation shortcuts (excludes groups - they're handled separately)
defineShortcuts(
  extractShortcuts([
    ...navigationItems.value.filter((item) => item.kbds),
    ...residentsItems.value.filter((item) => typeof item.id === "string"), // Only Residents and Children
    ...settingsItems.value.filter((item) => item.kbds),
  ]),
);

return (_ctx: any,_cache: any) => {
  const _component_SubscriptionBanner = _resolveComponent("SubscriptionBanner")!
  const _component_UColorModeImage = _resolveComponent("UColorModeImage")!
  const _component_NuxtLink = _resolveComponent("NuxtLink")!
  const _component_UDashboardSidebarCollapse = _resolveComponent("UDashboardSidebarCollapse")!
  const _component_GroupDropdown = _resolveComponent("GroupDropdown")!
  const _component_UDashboardSearchButton = _resolveComponent("UDashboardSearchButton")!
  const _component_UIcon = _resolveComponent("UIcon")!
  const _component_UBadge = _resolveComponent("UBadge")!
  const _component_UNavigationMenu = _resolveComponent("UNavigationMenu")!
  const _component_USeparator = _resolveComponent("USeparator")!
  const _component_UButton = _resolveComponent("UButton")!
  const _component_UTooltip = _resolveComponent("UTooltip")!
  const _component_UDashboardSidebar = _resolveComponent("UDashboardSidebar")!
  const _component_LazyUDashboardSearch = _resolveComponent("LazyUDashboardSearch")!
  const _component_ClientOnly = _resolveComponent("ClientOnly")!
  const _component_LazyNeedNewPassword = _resolveComponent("LazyNeedNewPassword")!
  const _component_UModal = _resolveComponent("UModal")!
  const _component_UDashboardGroup = _resolveComponent("UDashboardGroup")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_SubscriptionBanner),
    _createVNode(_component_UDashboardGroup, {
      unit: "rem",
      class: "bg-neutral-100 dark:bg-[#1E2021]"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_UDashboardSidebar, {
          collapsed: _unref(auth).sidebarCollapsed,
          "onUpdate:collapsed": _cache[0] || (_cache[0] = ($event: any) => ((_unref(auth).sidebarCollapsed) = $event)),
          collapsible: "",
          resizable: false,
          "min-size": 0,
          "max-size": 35,
          "default-size": 20,
          toggle: {
        color: 'primary',
        variant: 'subtle',
        class: 'rounded-full flex items-center justify-center',
      },
          ui: {
        root: `relative hidden lg:flex flex-col max-h-[98vh] min-w-16  shrink-0 transition-width duration-250 ease-linear border-none my-1.5 ${_unref(auth).sidebarCollapsed ? 'flex justify-start rounded-lg ml-2 min-h-[90vh] ' : 'w-50'}`,
        body: 'flex flex-col gap-4 flex-1 overflow-y-auto px-2.5 py-0 pb-2.5 ring-0 border-none',
        header: `${!_unref(auth).sidebarCollapsed ? 'flex items-center justify-between w-full' : 'w-full'}`,
        // toggle: 'absolute left-0 top-0 bottom-0 flex items-center justify-center p-2',
      }
        }, {
          header: _withCtx(() => [
            _createVNode(_component_NuxtLink, {
              to: "/user/",
              class: _normalizeClass([
            'flex items-center gap-x-2 align-middle w-full',
            !_unref(auth).sidebarCollapsed ? 'justify-start gap-x-2' : 'justify-between w-full',
          ])
            }, {
              default: _withCtx(() => [
                _createVNode(_component_UColorModeImage, {
                  light: "/images/logo_light.svg",
                  dark: "/images/logo_dark.svg",
                  class: "w-[32px] h-[32px] max-w-[32px] min-w-[32px]"
                })
              ]),
              _: 1
            }, 8, ["class"]),
            (!_unref(auth).sidebarCollapsed)
              ? (_openBlock(), _createBlock(_component_UDashboardSidebarCollapse, {
                  key: 0,
                  variant: "subtle",
                  class: "ml-auto"
                }))
              : _createCommentVNode("", true)
          ]),
          default: _withCtx(() => [
            (_unref(auth).sidebarCollapsed)
              ? (_openBlock(), _createBlock(_component_UDashboardSidebarCollapse, {
                  key: 0,
                  variant: "subtle",
                  class: "flex items-center justify-center w-full"
                }))
              : _createCommentVNode("", true),
            (!_unref(noGroupAccess))
              ? (_openBlock(), _createBlock(_component_GroupDropdown, {
                  key: 1,
                  collapsed: _unref(auth).sidebarCollapsed,
                  class: "w-3/5"
                }, null, 8, ["collapsed"]))
              : _createCommentVNode("", true),
            _createVNode(_component_UDashboardSearchButton, {
              collapsed: _unref(auth).sidebarCollapsed,
              class: "px-2.5"
            }, null, 8, ["collapsed"]),
            _createVNode(_component_UNavigationMenu, {
              items: _unref(auth).orga && !_unref(noGroupAccess) ? _unref(sidebarLinks) : noOrgaLinks,
              variant: "mainmenu",
              orientation: "vertical",
              tooltip: "",
              popover: "",
              collapsed: _unref(auth).sidebarCollapsed,
              ui: {
            link: [
              'data-active:bg-gradient-to-r from-amber-100 to-fuchsia-100 dark:from-slate-400 dark:to-violet-300 rounded-md',
              'hover:text-neutral-800 dark:hover:text-neutral-200 my-2',
              `${_unref(auth).sidebarCollapsed ? 'flex items-center justify-start w-full' : 'flex items-center justify-start w-full'}`,
            ],
            root: 'relative flex gap-1.5 [&>div]:min-w-0',
            childList: 'border-none ml-0 gap-y-2',
            childItem:
              'ps-1.5 -ms-px px-1 p-0 hover:text-neutral-800 dark:hover:text-neutral-200 gap-y-2',
          }
            }, _createSlots({ _: 2 }, [
              (_unref(auth).sidebarCollapsed)
                ? {
                    name: "comms",
                    fn: _withCtx(({ item }) => [
                      _createVNode(_component_UIcon, {
                        name: item.icon,
                        class: "iconify shrink-0 size-5 text-dimmed"
                      }, null, 8, ["name"]),
                      (item.badge)
                        ? (_openBlock(), _createBlock(_component_UBadge, {
                            key: 0,
                            id: "comms_badge",
                            color: "group_red",
                            variant: "solid",
                            size: "xs",
                            class: "absolute left-8 top-0"
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(item.badge), 1)
                            ]),
                            _: 2
                          }, 1024))
                        : _createCommentVNode("", true)
                    ]),
                    key: "0"
                  }
                : undefined
            ]), 1032, ["items", "collapsed", "ui"]),
            (_unref(auth).orga)
              ? (_openBlock(), _createBlock(_component_USeparator, { key: 2 }))
              : _createCommentVNode("", true),
            (_unref(auth).orga && !_unref(noGroupAccess))
              ? (_openBlock(), _createBlock(_component_UNavigationMenu, {
                  key: 3,
                  items: _unref(residentsSection),
                  variant: "mainmenu",
                  orientation: "vertical",
                  tooltip: "",
                  popover: "",
                  collapsed: _unref(auth).sidebarCollapsed,
                  ui: {
            link: [
              'data-active:bg-gradient-to-r from-amber-100 to-fuchsia-100 dark:from-slate-400 dark:to-violet-300 rounded-md',
              'hover:text-neutral-800 dark:hover:text-neutral-200 my-2',
              `${_unref(auth).sidebarCollapsed ? 'flex items-center justify-start w-full' : 'flex items-center justify-start w-full'}`,
            ],
            root: 'relative flex gap-1.5 [&>div]:min-w-0',
            childList: 'border-none ml-0 gap-y-2',
            childItem:
              'ps-1.5 -ms-px px-1 p-0 hover:text-neutral-800 dark:hover:text-neutral-200 gap-y-2 transition-width duration-250 ease-linear',
          }
                }, {
                  "customChild-leading": _withCtx(({ item }) => [
                    _createVNode(_component_UTooltip, {
                      text: item.tooltip.text,
                      shortcuts: item.tooltip.shortcuts
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_UButton, {
                          icon: item.icon,
                          color: item.color,
                          variant: "ghost",
                          size: "md",
                          class: _normalizeClass(`text-neutral-800 dark:text-neutral-200 gap-0 p-0 text-${item.color} hover:text-neutral-800 dark:hover:text-neutral-200`)
                        }, null, 8, ["icon", "color", "class"])
                      ]),
                      _: 2
                    }, 1032, ["text", "shortcuts"])
                  ]),
                  _: 1
                }, 8, ["items", "collapsed", "ui"]))
              : _createCommentVNode("", true),
            _createVNode(_component_UNavigationMenu, {
              items: _unref(footerLinks),
              orientation: "vertical",
              class: "mt-auto",
              variant: "mainmenu",
              tooltip: "",
              popover: "",
              ui: {
            link: 'data-active:bg-gradient-to-r from-amber-100 to-fuchsia-100 dark:from-slate-400 dark:to-violet-300 rounded-full',
          }
            }, null, 8, ["items"])
          ]),
          footer: _withCtx(() => [
            _createVNode(_component_UIcon, {
              name: "i-lucide-wifi-off",
              class: "absolute bottom-0 right-0 size-0 w-0 h-0 hidden"
            })
          ]),
          _: 1
        }, 8, ["collapsed", "ui"]),
        _renderSlot(_ctx.$slots, "default"),
        _createVNode(_component_ClientOnly, null, {
          default: _withCtx(() => [
            _createVNode(_component_LazyUDashboardSearch, { groups: _unref(commandGroups) }, null, 8, ["groups"])
          ]),
          _: 1
        }),
        _createVNode(_component_UModal, {
          open: _unref(newPasswordModalOpen),
          "onUpdate:open": _cache[1] || (_cache[1] = ($event: any) => (_isRef(newPasswordModalOpen) ? (newPasswordModalOpen).value = $event : null)),
          dismissible: false
        }, {
          content: _withCtx(() => [
            _createVNode(_component_LazyNeedNewPassword)
          ]),
          _: 1
        }, 8, ["open"])
      ]),
      _: 3
    })
  ], 64))
}
}

})